import currencies from '@/locales/currencies.json'

/**
 * Валюта страны инстанса.
 *
 * Данные — `front/locales/currencies.json` (страна → валюта), выбор страны —
 * переменная окружения `DEFAULT_COUNTRY`. Валюта не зависит от языка интерфейса:
 * какой бы язык ни выбрал пользователь, платит он деньгами страны инстанса.
 * Новый язык добавляется правками в `/locales`, код менять не нужно.
 */

const DEFAULT_COUNTRY = currencies.default
const COUNTRIES = currencies.countries || {}

/**
 * Код страны инстанса (`DEFAULT_COUNTRY`), приведённый к нижнему регистру.
 * @returns {string}
 */
export function getCountry() {
  const country = process.env.DEFAULT_COUNTRY

  return country ? String(country).toLowerCase() : DEFAULT_COUNTRY
}

/**
 * Данные валюты страны инстанса.
 * @returns {{ code: string, symbol: string, numberLocale: string, locale: string }}
 */
export function getCurrency() {
  return COUNTRIES[getCountry()] || COUNTRIES[DEFAULT_COUNTRY]
}

/** Код валюты страны инстанса: RUB / MNT. */
export function getCurrencyCode() {
  return getCurrency().code
}

/** Символ валюты страны инстанса: ₽ / ₮. */
export function getCurrencySymbol() {
  return getCurrency().symbol
}

/**
 * Число в формате страны инстанса (разделитель разрядов — пробел).
 * Нечисловое значение возвращается как есть, чтобы в интерфейсе не появилось "NaN".
 * @param {number|string} value
 * @param {{ maximumFractionDigits?: number }} [options]
 * @returns {string}
 */
export function formatAmount(value, { maximumFractionDigits = 2 } = {}) {
  const number = Number(String(value ?? '').replace(',', '.'))

  if (!Number.isFinite(number)) return String(value ?? '')

  // Разделитель разрядов приводим к пробелу: так числа записаны в переводах обоих языков.
  return new Intl.NumberFormat(getCurrency().numberLocale, {
    maximumFractionDigits,
  })
    .formatToParts(number)
    .map((part) => (part.type === 'group' ? ' ' : part.value))
    .join('')
}

/**
 * Сумма с символом валюты страны инстанса: "5 000 ₮" или "5 000 ₽".
 * @param {number|string} value
 * @param {{ maximumFractionDigits?: number }} [options]
 * @returns {string}
 */
export function formatCurrency(value, options) {
  return `${formatAmount(value, options)} ${getCurrencySymbol()}`
}

export default {
  getCountry,
  getCurrency,
  getCurrencyCode,
  getCurrencySymbol,
  formatAmount,
  formatCurrency,
}
